import type { Breakpoint } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Link from 'next/link';
import Image from 'next/image';
import { Scrollbar } from '../scrollbar';
import { NavSectionMini, NavSectionProps, NavSectionVertical } from '../nav-section';
import { hideScrollY, varAlpha } from '../theme/styles';
import { NavToggleButton } from './components/nav-toggle-button';
import SidebarLogo from '../logo/SidebarLogo';
import { selfUrlToHomeHref } from '@/lib/appBasePath';

export type NavVerticalProps = NavSectionProps & {
  isNavMini: boolean;
  layoutQuery: Breakpoint;
  onToggleNav: () => void;
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
};

export function NavVertical({
  sx,
  data,
  slots,
  isNavMini,
  layoutQuery,
  onToggleNav,
  ...other
}: NavVerticalProps) {
  const theme = useTheme();
  const appUrl = process.env.NEXT_PUBLIC_SELF_URL;
  const appVersion = process.env.NEXT_PUBLIC_APP_VERSION;

  const renderNavVertical = (
    <>
      {slots?.topArea ?? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            pt: 1.5,
            pb: 1.5,
            flexShrink: 0,
          }}
        >
          <Link href={selfUrlToHomeHref(appUrl)} style={{ display: 'inline-flex' }}>
            <span>
              <Image
                alt="boostt.ai logo"
                src={theme.palette.mode === 'dark' ? '/v2/images/logo-on-dark.png' : '/v2/images/boosttai.png'}
                width={140}
                height={35}
                style={{ objectFit: 'contain' }}
              />
            </span>
          </Link>
        </Box>
      )}

      <Scrollbar fillContent sx={{ px: '10px' }}>
        <NavSectionVertical data={data} sx={{ px: 0, flex: '1 1 auto', gap: '2px' }} {...other} />
      </Scrollbar>

      {slots?.bottomArea}

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', display: 'block', textAlign: 'left', letterSpacing: 0.5 }}
        >
          beta v{appVersion}
        </Typography>
      </Box>
    </>
  );

  const renderNavMini = (
    <>
      {slots?.topArea ?? (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            height: 'var(--layout-nav-horizontal-height)', 
            pt: 1, // Added back some padding
            pb: 0.5 
          }}
        >
          <SidebarLogo appUrl={appUrl} />
        </Box>
      )}

      <NavSectionMini
        data={data}
        sx={{ pb: 1, px: 0.25, ...hideScrollY, flex: '1 1 auto', overflowY: 'auto' }}
        {...other}
      />

      {slots?.bottomArea}

      <Box
        sx={{
          py: 1.5,
          borderTop: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', fontSize: 9 }}
        >
          v{appVersion}
        </Typography>
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        top: 0,
        left: 0,
        height: 1,
        display: 'none',
        position: 'fixed',
        flexDirection: 'column',
        bgcolor: 'var(--bg-canvas)',
        zIndex: 'var(--layout-nav-zIndex)',
        width: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
        borderRight: '1px solid var(--line)',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-body)',
        lineHeight: 1.5,
        WebkitFontSmoothing: 'antialiased',
        // HTML Command Center type scale (mock uses --text-micro: 10px)
        '--text-micro': '10px',
        transition: theme.transitions.create(['width'], {
          easing: 'var(--layout-transition-easing)',
          duration: 'var(--layout-transition-duration)',
        }),
        [theme.breakpoints.up(layoutQuery)]: {
          display: 'flex',
        },
        ...sx,
      }}
    >
      <NavToggleButton
        isNavMini={isNavMini}
        onClick={onToggleNav}
        sx={{
          display: 'none',
          [theme.breakpoints.up(layoutQuery)]: {
            display: 'inline-flex',
          },
        }}
      />
      {isNavMini ? renderNavMini : renderNavVertical}
    </Box>
  );
}
